import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AuthStateService } from '../../../../../core/services/auth-state/auth-state-service.service';

type Matrix = Record<string, Record<string, number | string>>;

interface MatrixConfig {
  key: string;
  label: string;
  form: FormGroup;
  originalMatrix: (number | string)[][];
}

@Component({
  selector: 'app-triple-matrix',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    FormsModule,
    CommonModule,
  ],
  templateUrl: './triple-matrix.component.html',
  styleUrl: './triple-matrix.component.css',
})
export class TripleMatrixComponent {
  @Input() campaignData?: any;
  @Input() ivaEnabled = true;

  matrices: MatrixConfig[];
  taxRate: number = 0.21;
  priceMatrixMask: boolean[][] = [];

  constructor(
    private fb: FormBuilder,
    private authStateService: AuthStateService,
    private cdr: ChangeDetectorRef,
  ) {
    this.matrices = [
      {
        key: 'quotes_matrix_individual',
        label: 'Individual',
        form: this.createEmptyMatrixForm(),
        originalMatrix: []
      },
      {
        key: 'quotes_matrix_self_employed',
        label: 'Autónomo',
        form: this.createEmptyMatrixForm(),
        originalMatrix: []
      },
      {
        key: 'quotes_matrix_organization',
        label: 'Organización',
        form: this.createEmptyMatrixForm(),
        originalMatrix: []
      },
    ];
    this.taxRate = this.authStateService.getSupplierTax() || 0.21;
  }

  private createEmptyMatrixForm(): FormGroup {
    return this.fb.group({
      headers: this.fb.array<FormControl>([]),
      rows: this.fb.array<FormGroup>([])
    });
  }

  getHeaders(form: FormGroup): FormArray<FormControl> {
    return form.get('headers') as FormArray;
  }

  getRows(form: FormGroup): FormArray<FormGroup> {
    return form.get('rows') as FormArray;
  }

  getCellControl(form: FormGroup, rowIndex: number, cellIndex: number): FormControl {
    const row = this.getRows(form).at(rowIndex) as FormGroup;
    return (row.get('cells') as FormArray).at(cellIndex) as FormControl;
  }

  /**
   * Receives the main price matrix and builds a mask of which cells have values.
   * Disables offer cells where the price matrix has no value.
   */
  syncPriceMatrix(priceRows: FormArray<FormGroup>, priceHeaders: FormArray<FormControl>): void {
    this.priceMatrixMask = priceRows.controls.map((rowGroup) => {
      const cells = rowGroup.get('cells') as FormArray;
      return cells.controls.map(control => {
        const val = control.value;
        return val !== null && val !== '' && val !== undefined;
      });
    });

    for (const matrix of this.matrices) {
      const rowsArr = this.getRows(matrix.form);
      rowsArr.controls.forEach((rowGroup, rowIndex) => {
        const cells = rowGroup.get('cells') as FormArray;
        cells.controls.forEach((control, cellIndex) => {
          const hasPrice = this.priceMatrixMask[rowIndex]?.[cellIndex] ?? false;
          if (!hasPrice) {
            control.setValue(null);
            control.disable();
          } else {
            control.enable();
          }
        });
      });
    }
  }

  isCellEnabled(rowIndex: number, cellIndex: number): boolean {
    return this.priceMatrixMask[rowIndex]?.[cellIndex] ?? true;
  }

  /**
   * Called by the parent to sync dimensions with the main matrix.
   * Headers and row labels are read-only in this component.
   */
  syncDimensions(headers: string[], rowLabels: string[]): void {
    for (const matrix of this.matrices) {
      this.syncMatrixDimensions(matrix, headers, rowLabels);
    }
  }

  private syncMatrixDimensions(matrix: MatrixConfig, newHeaders: string[], newRowLabels: string[]): void {
    const headersArr = this.getHeaders(matrix.form);
    const rowsArr = this.getRows(matrix.form);

    // Save existing cell values keyed by (label, header)
    const existingValues: Record<string, Record<string, any>> = {};
    rowsArr.controls.forEach(row => {
      const label = row.get('label')?.value;
      const cells = (row.get('cells') as FormArray).controls;
      headersArr.controls.forEach((h, idx) => {
        if (!existingValues[label]) existingValues[label] = {};
        existingValues[label][h.value] = cells[idx]?.value ?? '';
      });
    });

    // Rebuild headers (read-only)
    headersArr.clear();
    newHeaders.forEach(h => headersArr.push(new FormControl(h)));

    // Rebuild rows with preserved cell values
    rowsArr.clear();
    newRowLabels.forEach(label => {
      const cells = newHeaders.map(h => {
        const val = existingValues[label]?.[h] ?? '';
        return this.fb.control(val);
      });
      rowsArr.push(this.fb.group({
        label: this.fb.control(label),
        cells: this.fb.array(cells)
      }));
    });
  }

  /**
   * Load matrix data from campaignData for each of the 3 matrices.
   * Uses the already-synced dimensions (from the main matrix) and only fills cell values.
   */
  loadFromCampaignData(): void {
    for (const matrix of this.matrices) {
      const data = this.campaignData?.[matrix.key];
      if (data) {
        this.populateMatrixValues(matrix, data);
      }
    }
  }

  private populateMatrixValues(matrix: MatrixConfig, data: Matrix): void {
    const headersArr = this.getHeaders(matrix.form);
    const rowsArr = this.getRows(matrix.form);

    matrix.originalMatrix = [];

    rowsArr.controls.forEach(rowGroup => {
      const km = rowGroup.get('label')?.value;
      const cells = rowGroup.get('cells') as FormArray;
      const originalRow: (number | string)[] = [];

      headersArr.controls.forEach((hdrCtrl, idx) => {
        const month = hdrCtrl.value;
        const value = data[month]?.[km] ?? null;
        originalRow.push(value);
        cells.at(idx).setValue(value);
      });

      matrix.originalMatrix.push(originalRow);
    });
  }

  removeIva(): void {
    for (const matrix of this.matrices) {
      matrix.originalMatrix = this.getRows(matrix.form).controls.map((rowGroup: FormGroup) => {
        const cells = rowGroup.get('cells') as FormArray;
        return cells.controls.map(control => control.value);
      });

      this.getRows(matrix.form).controls.forEach((rowGroup, rowIndex) => {
        const cells = rowGroup.get('cells') as FormArray;
        cells.controls.forEach((control, cellIndex) => {
          const originalValue = matrix.originalMatrix[rowIndex]?.[cellIndex];
          const numVal = parseFloat(String(originalValue));
          if (!isNaN(numVal)) {
            const withoutIva = (numVal / (1 + this.taxRate)).toFixed(2);
            control.setValue(withoutIva);
          }
          control.disable();
        });
      });
    }
  }

  applyIva(): void {
    for (const matrix of this.matrices) {
      this.getRows(matrix.form).controls.forEach((rowGroup, rowIndex) => {
        const cells = rowGroup.get('cells') as FormArray;
        cells.controls.forEach((control, cellIndex) => {
          const originalValue = matrix.originalMatrix[rowIndex]?.[cellIndex];
          const hasPrice = this.priceMatrixMask[rowIndex]?.[cellIndex] ?? true;
          control.setValue(originalValue);
          if (hasPrice) {
            control.enable();
          } else {
            control.disable();
          }
        });
      });
    }
  }

  private obtenerMatriz(matrixConfig: MatrixConfig): Matrix {
    const result: Matrix = {};
    const headersArr = this.getHeaders(matrixConfig.form);
    const rowsArr = this.getRows(matrixConfig.form);

    rowsArr.controls.forEach(rowCtrl => {
      const rowKey = rowCtrl.get('label')!.value;
      const cells = (rowCtrl.get('cells') as FormArray).value;

      headersArr.controls.forEach((hdrCtrl, idx) => {
        const colKey = hdrCtrl.value.replace(/\s*meses?$/i, '').trim();
        const cellValue = cells[idx];

        if (cellValue !== '' && cellValue !== null && cellValue !== undefined) {
          if (!result[colKey]) result[colKey] = {};
          result[colKey][rowKey] = String(cellValue);
        }
      });
    });
    return result;
  }

  private adjust(source: Matrix): Matrix {
    const clone: Matrix = {};
    Object.entries(source).forEach(([months, rows]) => {
      clone[months] = {};
      Object.entries(rows).forEach(([km, value]) => {
        const numVal = parseFloat(String(value));
        clone[months][km] = !isNaN(numVal)
          ? (numVal / (1 + this.taxRate)).toFixed(2)
          : value;
      });
    });
    return clone;
  }

  /**
   * Returns an object with the 3 matrices in the same format as the main matrix.
   * Each matrix has both _base (without IVA) and _total (with IVA) variants.
   */
  getValues(currentIvaEnabled: boolean): any {
    const result: any = {};

    for (const matrix of this.matrices) {
      let matrixTotal: Matrix;

      if (currentIvaEnabled) {
        matrixTotal = this.obtenerMatriz(matrix);
      } else {
        // Temporarily restore original values to get with-IVA matrix
        this.getRows(matrix.form).controls.forEach((rowGroup, rowIndex) => {
          const cells = rowGroup.get('cells') as FormArray;
          cells.controls.forEach((control, cellIndex) => {
            const originalValue = matrix.originalMatrix[rowIndex]?.[cellIndex];
            control.setValue(originalValue);
            control.enable();
          });
        });
        matrixTotal = this.obtenerMatriz(matrix);
      }

      result[matrix.key] = matrixTotal;
    }

    return result;
  }

  getOtherMatrices(current: MatrixConfig): MatrixConfig[] {
    return this.matrices.filter(m => m.key !== current.key);
  }

  copyToMatrix(source: MatrixConfig, target: MatrixConfig): void {
    const sourceCellValues = (source.form.get('rows') as FormArray).getRawValue();
    const targetRows = this.getRows(target.form);

    sourceCellValues.forEach((sourceRow: any, rowIndex: number) => {
      const targetRow = targetRows.controls[rowIndex];
      if (!targetRow) return;
      const targetCells = targetRow.get('cells') as FormArray;
      sourceRow.cells.forEach((val: any, cellIndex: number) => {
        const hasPrice = this.priceMatrixMask[rowIndex]?.[cellIndex] ?? true;
        if (targetCells.at(cellIndex) && hasPrice) {
          targetCells.at(cellIndex).setValue(val);
        }
      });
    });

    target.originalMatrix = source.originalMatrix.map(row => [...row]);
    this.cdr.detectChanges();
  }

  trackByIndex(_index: number, _item: unknown) { return _index; }
}
